/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useRef, useState } from 'react';

import { BackgroundVideo } from '@wit/vtv-x-foundation-lib';
import PlatformUtils from 'services/utils/PlatformUtils';
import HeroOverlay from 'assets/images/background/hero_overlay.png';

import Nagra from '../Player/Nagra';
import { ImageLoader } from '..';

const STREAM_INDEX_TO_PLAY = 0;

interface Props {
  backgroundImage?: string;
  backgroundVideo?: BackgroundVideo;
}

export default function Overlay({ backgroundImage, backgroundVideo }: Props): JSX.Element {
  const hasBackgroundVideo = !!backgroundVideo;
  const [isBckVideoAbleToRender, setIsBckVideoAbleToRender] = useState(
    hasBackgroundVideo && !PlatformUtils.isMobileDevice()
  );
  const [isLoadingBckVideo, setIsLoadingBckVideo] = useState(true);

  const nagraPlayerRef = useRef<Nagra>(null);

  useEffect(() => {
    if (!hasBackgroundVideo || isLoadingBckVideo) {
      return;
    }

    nagraPlayerRef.current?.play();
  }, [hasBackgroundVideo, isLoadingBckVideo]);

  useEffect(() => {
    if (!hasBackgroundVideo || !backgroundVideo?.streams[STREAM_INDEX_TO_PLAY]?.url) {
      return;
    }

    nagraPlayerRef.current?.setStream(
      backgroundVideo?.streams[STREAM_INDEX_TO_PLAY]?.url,
      undefined,
      backgroundVideo?.isMuted || false,
      backgroundVideo?.onLoop
    );
  }, [hasBackgroundVideo, backgroundVideo?.isMuted, backgroundVideo?.onLoop, backgroundVideo?.streams]);

  return (
    <>
      <div className="hero-overlay-container" id="OverlayHero_wrapper">
        {backgroundImage && (!isBckVideoAbleToRender || isLoadingBckVideo) ? (
          <ImageLoader className="hero-absolute-image content-fadein" src={backgroundImage} bemModifier="hero" />
        ) : null}
        {isBckVideoAbleToRender ? (
          <div id="OverlayHero_player" className={`player visible ${isLoadingBckVideo ? 'loading' : 'content-fadein'}`}>
            <Nagra
              ref={nagraPlayerRef}
              onError={() => setIsBckVideoAbleToRender(false)}
              onStreamReady={() => setIsLoadingBckVideo(false)}
              onEnd={() => setIsBckVideoAbleToRender(false)}
            />
          </div>
        ) : null}
        <div className="hero-overlay-mask" />
      </div>
    </>
  );
}
